import { useState } from 'react';
import type { MicPermission } from '../../call/bridge.ts';
import { iphoneSafari, iphoneHomeScreen } from '../../call/iphone.ts';
import { useWorkspace } from '../../state/workspace.tsx';

export function SafariMicrophoneSteps() {
  return <div className="microphone-steps" aria-label="Allow microphone in Safari">
    <figure>
      <img src="/help/safari-page-menu.svg" width="280" height="108" alt="Tap the aA page menu, then Website Settings" />
      <figcaption>Tap Safari’s <strong>aA</strong> (or page menu), then <strong>Website Settings</strong>.</figcaption>
    </figure>
    <figure>
      <img src="/help/safari-microphone.svg" width="280" height="108" alt="In Website Settings choose Microphone, then Allow" />
      <figcaption>Tap <strong>Microphone: Allow</strong>, then <strong>Done</strong> and try again here.</figcaption>
    </figure>
  </div>;
}

export function MicrophoneNotice({ permission, iphone, request, error, compact = false }: {
  permission: MicPermission; iphone: boolean; request: () => Promise<void>; error?: string | null; compact?: boolean;
}) {
  const [pending, setPending] = useState(false);
  if (permission === 'granted' || permission === 'unsupported') return null;
  return <section className="notice notice-warn microphone-notice" aria-label="Microphone permission" data-phone-warning="needs_microphone">
    <div className="microphone-action">
      <div><strong>{permission === 'denied' ? 'Microphone blocked' : 'Allow your microphone once'}</strong>
        <p>Incoming calls can ring. Allow the microphone to answer or make a call.{iphone ? ' Sidetone keeps it ready and muted between calls while this phone session is open.' : ''}</p>
      </div>
      <button type="button" className="btn" disabled={pending} aria-busy={pending || undefined} onClick={() => {
        setPending(true); void request().finally(() => setPending(false));
      }}>Allow microphone</button>
    </div>
    {error ? <p className="field-error" role="alert">{error}</p> : null}
    {iphone && permission === 'denied' && !compact ? <SafariMicrophoneSteps /> : null}
  </section>;
}

export function MicrophoneWarning({ compact = false }: { compact?: boolean }) {
  const ws = useWorkspace();
  if (!ws.device || ws.bridge.phone.state === 'stopped' || !ws.permissions.includes('devices:manage_own')) return null;
  return <MicrophoneNotice permission={ws.mic} iphone={iphoneSafari() || iphoneHomeScreen()} request={ws.requestMic} error={ws.deviceError?.title ?? null} compact={compact} />;
}

export function IPhoneInstallCard({ hint = false }: { hint?: boolean }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return hint && globalThis.localStorage?.getItem('sidetone:iphone-install-hint') === 'dismissed'; } catch { return false; }
  });
  if (dismissed || !iphoneSafari() || iphoneHomeScreen()) return null;
  return <section className={hint ? 'iphone-install-hint' : 'settings-tile'} aria-label="Install on iPhone">
    <h2 className="section-title">Install on iPhone</h2>
    <p>Tap Safari’s <strong>Share</strong> button (square with an up arrow), then <strong>Add to Home Screen</strong> and <strong>Add</strong>. Open Sidetone from the new icon.</p>
    <p className="field-hint">Allow the microphone when asked. Your iPhone controls permission between app opens; the first grant always needs your approval. Keep Sidetone open and available to receive calls.</p>
    {hint ? <button type="button" className="btn btn-small" onClick={() => {
      setDismissed(true);try { globalThis.localStorage?.setItem('sidetone:iphone-install-hint','dismissed'); } catch { /* session-only dismissal */ }
    }}>Got it</button> : null}
  </section>;
}

export function PhonePermissionBanner() {
  return <div className="phone-permissions"><MicrophoneWarning /><IPhoneInstallCard hint /></div>;
}
